import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, LogOut, AlertCircle } from 'lucide-react';
import { getVersionString } from '../config/version';

const AnwaltDashboardPage = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const lawyerName = localStorage.getItem('lawyer_name');
  const lawyerToken = localStorage.getItem('lawyer_token');

  useEffect(() => {
    if (!lawyerToken) {
      navigate('/anwalt/login');
      return;
    }
    loadCases();
  }, []);

  const loadCases = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webakte/list/my-cases`, {
        headers: { 'Authorization': `Bearer ${lawyerToken}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      const data = await response.json();
      setCases(data.cases || []);
    } catch (error) {
      console.error('Load failed:', error);
      if (error.message.includes('keine Akte')) {
        setCases([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('lawyer_token');
    localStorage.removeItem('lawyer_id');
    localStorage.removeItem('lawyer_email');
    localStorage.removeItem('lawyer_name');
    navigate('/anwalt/login');
  };

  const openRequestsCount = cases.filter(c => c.status === 'rueckfrage_offen').length;
  const newDocsCount = 0; // TODO: Implement

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Anwalt Portal</h1>
            <div className="flex items-center gap-6">
              <span className="text-sm text-gray-600">{lawyerName}</span>
              <button
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-900 text-sm font-medium inline-flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Abmelden
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* KPIs */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Offene Akten</p>
                <p className="text-2xl font-bold text-gray-900">{cases.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Rückfragen</p>
                <p className="text-2xl font-bold text-gray-900">{openRequestsCount}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Neue Dokumente (7 Tage)</p>
                <p className="text-2xl font-bold text-gray-900">{newDocsCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Cases List */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Zugewiesene Akten</h2>
          
          {loading ? (
            <div className="text-center py-12">
              <p>Lädt...</p>
            </div>
          ) : cases.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Aktuell keine Akten zugewiesen</h3>
              <p className="text-gray-600">Akten erscheinen hier, sobald Sie vom Admin zugewiesen werden</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cases.map(c => (
                <div 
                  key={c.id} 
                  className="border-2 border-gray-200 rounded-xl p-6 hover:border-brand-green/40 transition-all hover:shadow-md cursor-pointer"
                  onClick={() => navigate(`/akte/${c.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <p className="text-xl font-bold text-gray-900">{c.license_plate || 'Kennzeichen nicht erfasst'}</p>
                        {c.status === 'rueckfrage_offen' && (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-300">
                            Rückfrage offen
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                        <span>{c.vehicle_manufacturer} {c.vehicle_model}</span>
                        {c.damage_date && <span>• {new Date(c.damage_date).toLocaleDateString('de-DE')}</span>}
                      </div>
                      {c.customer_name && (
                        <p className="text-sm text-gray-600">Kunde: {c.customer_name}</p>
                      )}
                    </div>
                    <button className="px-4 py-2 bg-brand-green hover:bg-brand-green-dark text-white text-sm font-semibold rounded-lg transition-colors">
                      Webakte öffnen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      
      {/* Version Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-100 border-t border-gray-200 py-2 px-4 text-center">
        <span className="text-xs text-gray-500">Anwaltsportal • {getVersionString()}</span>
      </div>
    </div>
  );
};

export default AnwaltDashboardPage;
